/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {Diff} from './github/diffTypes';

import {getPathForChange} from './utils';
import scrollToFileElement from './scrollToFileElement';
import {forceLoadDiffPathAtom} from './jotai/atoms';
import {gitHubPullRequestVersionDiffAtom, fileViewedStateAtom} from './jotai';
import {APP_HEADER_HEIGHT} from './constants';
import {Box, Text} from '@primer/react';
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react';
import {useAtomValue, useSetAtom} from 'jotai';
import {unwrap} from 'jotai/utils';
import React, {useCallback, useMemo, useState} from 'react';

const HEADER_HEIGHT = 100;
const TOTAL_HEADER_HEIGHT = HEADER_HEIGHT + APP_HEADER_HEIGHT;

// ------------------------------------------------------------------
// Tree data structure built from file paths
// ------------------------------------------------------------------

type TreeNode = {
  name: string;
  /** Full path from root (for directories, the directory path). */
  path: string;
  children: Map<string, TreeNode>;
  /** If this is a leaf, the change type. */
  changeType?: 'add' | 'remove' | 'modify' | 'rename';
};

function buildTree(diff: Diff): TreeNode {
  const root: TreeNode = {name: '', path: '', children: new Map()};

  for (const change of diff) {
    const fullPath = getPathForChange(change);
    const parts = fullPath.split('/');
    let current = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      let child = current.children.get(part);
      if (child == null) {
        child = {
          name: part,
          path: parts.slice(0, i + 1).join('/'),
          children: new Map(),
          changeType: isFile ? change.type : undefined,
        };
        current.children.set(part, child);
      }
      if (isFile) {
        child.changeType = change.type;
      }
      current = child;
    }
  }

  return root;
}

function sortedChildren(node: TreeNode): TreeNode[] {
  return [...node.children.values()].sort((a, b) => {
    // Directories first, then alphabetical
    const aIsDir = a.children.size > 0 && a.changeType == null;
    const bIsDir = b.children.size > 0 && b.changeType == null;
    if (aIsDir && !bIsDir) return -1;
    if (!aIsDir && bIsDir) return 1;
    return a.name.localeCompare(b.name);
  });
}

// ------------------------------------------------------------------
// Change-type badge (A / M / D)
// ------------------------------------------------------------------

function changeTypeBadge(type: 'add' | 'remove' | 'modify' | 'rename'): {label: string; color: string} {
  switch (type) {
    case 'add':
      return {label: 'A', color: '#6ab04c'};
    case 'remove':
      return {label: 'D', color: '#e74c3c'};
    case 'modify':
      return {label: 'M', color: '#f0c040'};
    case 'rename':
      return {label: 'R', color: '#8e44ad'};
  }
}

// ------------------------------------------------------------------
// Tree node components
// ------------------------------------------------------------------

function FileTreeNode({
  node,
  depth,
  onFileClick,
}: {
  node: TreeNode;
  depth: number;
  onFileClick: (path: string) => void;
}): React.ReactElement {
  const isFile = node.changeType != null;

  if (isFile) {
    return <FileLeaf node={node} depth={depth} onFileClick={onFileClick} />;
  }

  return <DirectoryNode node={node} depth={depth} onFileClick={onFileClick} />;
}

function DirectoryNode({
  node,
  depth,
  onFileClick,
}: {
  node: TreeNode;
  depth: number;
  onFileClick: (path: string) => void;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(true);
  const toggle = useCallback(() => setExpanded(prev => !prev), []);
  const children = useMemo(() => sortedChildren(node), [node]);

  return (
    <>
      <Box
        as="button"
        onClick={toggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          bg: 'transparent',
          border: 'none',
          cursor: 'pointer',
          px: 2,
          py: '3px',
          pl: `${depth * 16 + 8}px`,
          fontSize: '12px',
          color: 'fg.default',
          ':hover': {bg: 'canvas.subtle'},
          fontFamily: 'mono',
          textAlign: 'left',
        }}>
        {expanded ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
        <Text sx={{fontWeight: 'bold', fontSize: '12px'}}>{node.name}</Text>
      </Box>
      {expanded &&
        children.map(child => (
          <FileTreeNode key={child.path} node={child} depth={depth + 1} onFileClick={onFileClick} />
        ))}
    </>
  );
}

function FileLeaf({
  node,
  depth,
  onFileClick,
}: {
  node: TreeNode;
  depth: number;
  onFileClick: (path: string) => void;
}): React.ReactElement {
  const badge = node.changeType != null ? changeTypeBadge(node.changeType) : null;
  const viewedState = useAtomValue(fileViewedStateAtom(node.path));
  const isViewed = viewedState === 'VIEWED';
  const onClick = useCallback(() => onFileClick(node.path), [node.path, onFileClick]);

  return (
    <Box
      as="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        bg: 'transparent',
        border: 'none',
        cursor: 'pointer',
        px: 2,
        py: '3px',
        pl: `${depth * 16 + 8}px`,
        fontSize: '12px',
        color: isViewed ? 'fg.muted' : 'fg.default',
        opacity: isViewed ? 0.6 : 1,
        ':hover': {bg: 'actionListItem.default.hoverBg', opacity: 1},
        fontFamily: 'mono',
        textAlign: 'left',
      }}>
      {badge != null && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            fontSize: '10px',
            fontWeight: 'bold',
            fontFamily: 'normal',
            color: '#fff',
            bg: badge.color,
            flexShrink: 0,
          }}>
          {badge.label}
        </Box>
      )}
      <Text sx={{fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
        {node.name}
      </Text>
    </Box>
  );
}

// ------------------------------------------------------------------
// Main sidebar component
// ------------------------------------------------------------------

const unwrappedDiffAtom = unwrap(gitHubPullRequestVersionDiffAtom, prev => prev ?? null);

export default function FileTreeSidebar(): React.ReactElement {
  const diffResult = useAtomValue(unwrappedDiffAtom);
  const setForceLoadDiffPath = useSetAtom(forceLoadDiffPathAtom);

  const diff: Diff | null = diffResult?.diff ?? null;

  const tree = useMemo(() => (diff != null ? buildTree(diff) : null), [diff]);

  const onFileClick = useCallback(
    (path: string) => {
      // Force-load the diff (handles large files that aren't rendered yet)
      setForceLoadDiffPath(path);

      // Also scroll to the file in the main content area
      scrollToFileElement(path);
    },
    [setForceLoadDiffPath],
  );

  const rootChildren = useMemo(() => (tree != null ? sortedChildren(tree) : []), [tree]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height={`calc(100vh - ${TOTAL_HEADER_HEIGHT}px)`}
      overflow="auto"
>
      <Box
        sx={{
          px: 3,
          py: 2,
          fontSize: '12px',
          fontWeight: 'bold',
          color: 'fg.muted',
          borderBottom: '1px solid',
          borderColor: 'border.default',
          bg: 'canvas.subtle',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <span>Files{diff != null ? ` (${diff.length})` : ''}</span>
        <Text
          as="a"
          sx={{
            fontSize: '11px',
            fontWeight: 'normal',
            color: 'accent.fg',
            cursor: 'pointer',
            ':hover': {textDecoration: 'underline'},
          }}
          onClick={() => {
            const el = document.querySelector('[data-toc-container]');
            if (el != null) {
              el.scrollIntoView({behavior: 'auto', block: 'start'});
            }
          }}>
          Table of Contents
        </Text>
      </Box>
      <Box py={1}>
        {tree == null ? (
          <Box px={3} py={2}>
            <Text fontSize={12} color="fg.muted">
              No files to display
            </Text>
          </Box>
        ) : (
          rootChildren.map(child => (
            <FileTreeNode key={child.path} node={child} depth={0} onFileClick={onFileClick} />
          ))
        )}
      </Box>
    </Box>
  );
}
